import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getFirestore, doc, updateDoc, arrayUnion, onSnapshot, getDoc, deleteDoc, collection } from 'firebase/firestore';
import { ArrowLeftIcon, CheckCircleIcon, PlusIcon, DocumentTextIcon, UserIcon, CreditCardIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import InvoiceModal from '../../components/InvoiceModal';
import AgreementModal from '../../components/AgreementModal';

function ProjectDetails() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [clientDetails, setClientDetails] = useState(null);
  const [newTask, setNewTask] = useState({
    taskName: '',
    relativeDeadline: '',
    status: 'incomplete'
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Collaborators state
  const [showManageCollaborators, setShowManageCollaborators] = useState(false);
  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState('');
  const [selectedCollaboratorForInvoice, setSelectedCollaboratorForInvoice] = useState(null);
  const [showCollaboratorInvoiceModal, setShowCollaboratorInvoiceModal] = useState(false);

  useEffect(() => {
    const db = getFirestore();
    const projectRef = doc(db, 'projects', projectId);
    
    const unsubscribe = onSnapshot(projectRef, async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const projectData = { id: docSnapshot.id, ...docSnapshot.data() };
        setProject(projectData);
        
        // Fetch client details if clientId exists
        if (projectData.clientId) {
          const clientRef = doc(db, 'users', projectData.userId, 'clients', projectData.clientId);
          const clientSnap = await getDoc(clientRef);
          if (clientSnap.exists()) {
            setClientDetails(clientSnap.data());
          }
        }
        
        setLoading(false);
      } else {
        toast.error('Project not found');
        navigate('/app/projects');
      }
    });

    return () => unsubscribe();
  }, [projectId, navigate]);

  // Load clients
  useEffect(() => {
    if (!project?.userId) return;
    
    const db = getFirestore();
    const clientsRef = collection(db, 'users', project.userId, 'clients');
    const unsubscribe = onSnapshot(clientsRef, (snapshot) => {
      const clientsData = snapshot.docs.map(docSnapshot => ({
        id: docSnapshot.id,
        ...docSnapshot.data()
      }));
      setClients(clientsData);
    });

    return () => unsubscribe();
  }, [project?.userId]);

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTask.taskName.trim() || !newTask.relativeDeadline) return;

    const task = {
      taskName: newTask.taskName,
      relativeDeadline: parseInt(newTask.relativeDeadline),
      status: 'incomplete'
    };

    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'projects', projectId), {
        tasks: arrayUnion(task)
      });
      setNewTask({
        taskName: '',
        relativeDeadline: '',
        status: 'incomplete'
      });
      setShowAddTask(false);
      toast.success('Task added successfully');
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task');
    }
  };

  const handleToggleTask = async (taskIndex) => {
    const updatedTasks = [...(project.tasks || [])];
    updatedTasks[taskIndex] = {
      ...updatedTasks[taskIndex],
      status: updatedTasks[taskIndex].status === 'complete' ? 'incomplete' : 'complete'
    };

    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'projects', projectId), {
        tasks: updatedTasks
      });
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const calculateProgress = () => {
    if (!project?.tasks?.length) return 0;
    const completedTasks = project.tasks.filter(t => t.status === 'complete').length;
    return Math.round((completedTasks / project.tasks.length) * 100);
  };

  const handleDeleteProject = async () => {
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'projects', projectId));
      toast.success('Project deleted successfully');
      navigate('/app/projects');
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const handleUpdateClient = async (clientId) => {
    try {
      const db = getFirestore();
      const clientRef = doc(db, 'users', project.userId, 'clients', clientId);
      const clientSnap = await getDoc(clientRef);
      
      if (clientSnap.exists()) {
        const clientData = clientSnap.data();
        await updateDoc(doc(db, 'projects', projectId), {
          clientId: clientId,
          clientName: clientData.name
        });
        setShowClientModal(false);
        toast.success('Client updated successfully');
      }
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Failed to update client');
    }
  };

  // Collaborator management functions
  const handleAddCollaborator = async (e) => {
    e.preventDefault();
    if (!newCollaboratorEmail.trim() || !newCollaboratorEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (project.collaborators?.includes(newCollaboratorEmail.trim())) {
      toast.error('This collaborator is already added');
      return;
    }

    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'projects', projectId), {
        collaborators: arrayUnion(newCollaboratorEmail.trim())
      });
      setNewCollaboratorEmail('');
      toast.success('Collaborator added successfully');
    } catch (error) {
      console.error('Error adding collaborator:', error);
      toast.error('Failed to add collaborator');
    }
  };

  const handleRemoveCollaborator = async (email) => {
    try {
      const db = getFirestore();
      const updatedCollaborators = (project.collaborators || []).filter(c => c !== email);
      await updateDoc(doc(db, 'projects', projectId), {
        collaborators: updatedCollaborators
      });
      toast.success('Collaborator removed successfully');
    } catch (error) {
      console.error('Error removing collaborator:', error);
      toast.error('Failed to remove collaborator');
    }
  };

  const handleInvoiceCollaborator = (email) => {
    setSelectedCollaboratorForInvoice(email);
    setShowCollaboratorInvoiceModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-black p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
          </div>
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-white dark:bg-black p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/app/projects')}
            className="flex items-center gap-2 text-gray-500 dark:text-[#999999] hover:text-brand mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Projects</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl text-gray-900 dark:text-white font-medium">{project.projectName}</h1>
              <p className="text-gray-500 dark:text-[#999999] mt-1">
                Client: {project.clientName || 'No client assigned'}
                <button
                  onClick={() => setShowClientModal(true)}
                  className="ml-2 text-brand hover:text-brand/80 text-sm"
                >
                  {project.clientId ? 'Change' : 'Add Client'}
                </button>
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  project.status === 'In Progress'
                    ? 'bg-brand/10 text-brand'
                    : project.status === 'Completed'
                    ? 'bg-green-500/10 text-green-500'
                    : project.status === 'Review'
                    ? 'bg-yellow-500/10 text-yellow-500'
                    : 'bg-gray-500/10 text-gray-500'
                }`}
              >
                {project.status}
              </span>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                title="Delete Project"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Project Overview */}
        <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
          <div className="p-6 space-y-6">
            {/* Project Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-gray-900 dark:text-white font-medium">Project Details</h3>
                <div className="space-y-2 text-gray-500 dark:text-[#999999]">
                  <p>Status: {project.status}</p>
                  <p>Created: {project.createdAt?.toDate().toLocaleDateString()}</p>
                  <p>Due Date: {project.dueDate?.toDate().toLocaleDateString()}</p>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-gray-900 dark:text-white font-medium">Progress</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-[#999999]">Tasks Completed</span>
                    <span className="text-gray-900 dark:text-white">
                      {project.tasks?.filter(t => t.status === 'complete').length || 0} / {project.tasks?.length || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-[#1A1A1A] rounded-full h-2">
                    <div
                      className="bg-brand h-2 rounded-full transition-all"
                      style={{ width: `${calculateProgress()}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setShowInvoiceModal(true)}
                className="flex items-center justify-center gap-2 p-4 bg-gray-50 dark:bg-[#1A1A1A] rounded-xl hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
              >
                <CreditCardIcon className="w-5 h-5" />
                <span>Create Invoice</span>
              </button>
              <button
                onClick={() => setShowAgreementModal(true)}
                className="flex items-center justify-center gap-2 p-4 bg-gray-50 dark:bg-[#1A1A1A] rounded-xl hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
              >
                <DocumentTextIcon className="w-5 h-5" />
                <span>Create Agreement</span>
              </button>
              <button
                onClick={() => navigate(`/app/clients/${project?.clientId}`)}
                className="flex items-center justify-center gap-2 p-4 bg-gray-50 dark:bg-[#1A1A1A] rounded-xl hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
              >
                <UserIcon className="w-5 h-5" />
                <span>View Client</span>
              </button>
            </div>

            {/* Tasks Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-gray-900 dark:text-white font-medium">Tasks</h3>
                <button
                  onClick={() => setShowAddTask(true)}
                  className="flex items-center gap-2 text-brand hover:text-brand/80"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span>Add Task</span>
                </button>
              </div>

              {showAddTask && (
                <form onSubmit={handleAddTask} className="bg-gray-50 dark:bg-[#1A1A1A] rounded-xl p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-500 dark:text-[#999999] mb-1">Task Name</label>
                      <input
                        type="text"
                        value={newTask.taskName}
                        onChange={(e) => setNewTask(prev => ({ ...prev, taskName: e.target.value }))}
                        placeholder="Enter task name"
                        className="w-full px-4 py-2 rounded-xl bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-[#666666]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 dark:text-[#999999] mb-1">Days to Complete</label>
                      <input
                        type="number"
                        value={newTask.relativeDeadline}
                        onChange={(e) => setNewTask(prev => ({ ...prev, relativeDeadline: e.target.value }))}
                        placeholder="Enter number of days"
                        min="1"
                        className="w-full px-4 py-2 rounded-xl bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-[#666666]"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddTask(false)}
                      className="px-4 py-2 text-gray-500 dark:text-[#999999] hover:text-gray-700 dark:hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-brand text-white rounded-xl hover:bg-brand/90"
                    >
                      Add Task
                    </button>
                  </div>
                </form>
              )}

              <div className="space-y-2">
                {project.tasks?.map((task, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-[#1A1A1A] rounded-xl"
                  >
                    <button
                      onClick={() => handleToggleTask(index)}
                      className={task.status === 'complete' ? 'text-brand' : 'text-gray-400 dark:text-[#666666]'}
                    >
                      <CheckCircleIcon className="w-6 h-6" />
                    </button>
                    <div className="flex-1">
                      <span className={`block ${
                        task.status === 'complete'
                          ? 'line-through text-gray-500 dark:text-[#666666]'
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {task.taskName}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-[#999999]">
                        Due in {task.relativeDeadline} days
                      </span>
                    </div>
                  </div>
                ))}
                {!project.tasks?.length && (
                  <p className="text-center text-gray-500 dark:text-[#999999] py-4">
                    No tasks added yet
                  </p>
                )}
              </div>
            </div>

            {/* Client Details Section */}
            {clientDetails && (
              <div className="space-y-4">
                <h3 className="text-gray-900 dark:text-white font-medium">Client Information</h3>
                <div className="bg-gray-50 dark:bg-[#1A1A1A] rounded-xl p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-[#999999]">Name</p>
                      <p className="text-gray-900 dark:text-white">{clientDetails.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-[#999999]">Email</p>
                      <p className="text-gray-900 dark:text-white">{clientDetails.email}</p>
                    </div>
                    {clientDetails.company && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-[#999999]">Company</p>
                        <p className="text-gray-900 dark:text-white">{clientDetails.company}</p>
                      </div>
                    )}
                    {clientDetails.phone && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-[#999999]">Phone</p>
                        <p className="text-gray-900 dark:text-white">{clientDetails.phone}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Collaborators Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-gray-900 dark:text-white font-medium">Collaborators</h3>
                <button
                  onClick={() => setShowManageCollaborators(!showManageCollaborators)}
                  className="flex items-center gap-2 text-brand hover:text-brand/80"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span>Manage Collaborators</span>
                </button>
              </div>

              {/* Add Collaborator Form */}
              {showManageCollaborators && (
                <form onSubmit={handleAddCollaborator} className="bg-gray-50 dark:bg-[#1A1A1A] rounded-xl p-4 space-y-4">
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={newCollaboratorEmail}
                      onChange={(e) => setNewCollaboratorEmail(e.target.value)}
                      placeholder="Enter collaborator email"
                      className="flex-1 px-4 py-2 rounded-xl bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-[#666666]"
                    />
                    <button
                      type="submit"
                      className="px-4 py-2 bg-brand hover:bg-brand-hover text-black rounded-xl transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowManageCollaborators(false)}
                    className="text-sm text-gray-500 dark:text-[#999999] hover:text-gray-700 dark:hover:text-white"
                  >
                    Cancel
                  </button>
                </form>
              )}

              {/* Collaborators List */}
              <div className="space-y-2">
                {project.collaborators?.map((email, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#1A1A1A] rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-brand/20 rounded-full flex items-center justify-center">
                        <UserIcon className="w-4 h-4 text-brand" />
                      </div>
                      <span className="text-gray-900 dark:text-white">{email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleInvoiceCollaborator(email)}
                        className="px-3 py-1 bg-brand hover:bg-brand-hover text-black rounded-lg text-sm transition-colors"
                      >
                        Invoice
                      </button>
                      <button
                        onClick={() => handleRemoveCollaborator(email)}
                        className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {(!project.collaborators || project.collaborators.length === 0) && (
                  <p className="text-center text-gray-500 dark:text-[#999999] py-4">
                    No collaborators added yet. Add team members to collaborate on this project.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-6 z-[999]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-black rounded-3xl shadow-2xl w-full max-w-md p-6"
          >
            <h3 className="text-xl font-medium mb-4">Delete Project</h3>
            <p className="text-gray-500 dark:text-[#999999] mb-6">
              Are you sure you want to delete this project? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-500 dark:text-[#999999] hover:text-gray-700 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProject}
                className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600"
              >
                Delete Project
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Client Selection Modal */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-6 z-[999]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-black rounded-3xl shadow-2xl w-full max-w-md"
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-[#1A1A1A]">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-medium">Select Client</h3>
                <button
                  onClick={() => setShowClientModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-900 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-4">
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="px-6 py-4 max-h-[400px] overflow-y-auto">
              {clients
                .filter(client => 
                  client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  client.email.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(client => (
                  <button
                    key={client.id}
                    onClick={() => handleUpdateClient(client.id)}
                    className="w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-[#111111] rounded-xl mb-2 last:mb-0"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gradient-to-b from-brand to-[#FF9D5C] rounded-xl flex items-center justify-center text-black font-medium">
                        {client.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium">{client.name}</div>
                        <div className="text-sm text-gray-500 dark:text-[#999999]">{client.email}</div>
                      </div>
                    </div>
                  </button>
                ))}
              {clients.length === 0 && (
                <div className="text-center text-gray-500 dark:text-[#999999] py-8">
                  No clients found. Create a client first.
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Modals */}
      {showInvoiceModal && (
        <InvoiceModal
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          project={project}
          client={clientDetails}
        />
      )}

      {showAgreementModal && (
        <AgreementModal
          isOpen={showAgreementModal}
          onClose={() => setShowAgreementModal(false)}
          project={project}
          client={clientDetails}
        />
      )}

      {/* Collaborator Invoice Modal */}
      {showCollaboratorInvoiceModal && selectedCollaboratorForInvoice && (
        <InvoiceModal
          isOpen={showCollaboratorInvoiceModal}
          onClose={() => {
            setShowCollaboratorInvoiceModal(false);
            setSelectedCollaboratorForInvoice(null);
          }}
          project={project}
          client={{
            name: selectedCollaboratorForInvoice.split('@')[0], // Use email prefix as name
            email: selectedCollaboratorForInvoice
          }}
          isCollaboratorInvoice={true}
        />
      )}
    </div>
  );
}

export default ProjectDetails;
